/** Vastuumatriisi tab of the hanke card: one grid per tontti plus Hanketason tilat. */

import { useMemo } from "react";
import type { User } from "@/types";
import type { ProjectSpaceData } from "@/hooks/useProjectSpace";
import { useResponsibilityMatrix } from "@/hooks/useResponsibilityMatrix";
import { buildMatrixIndex } from "@/lib/responsibilityMatrix";
import { ResponsibilityMatrixGrid } from "./ResponsibilityMatrixGrid";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  projectId: string;
  projectSpace: ProjectSpaceData | null;
  users: User[];
  canEdit: boolean;
}

export function ResponsibilityMatrixTab({ projectId, projectSpace, users, canEdit }: Props) {
  const { data, loading, error, assign } = useResponsibilityMatrix(projectId);

  const allLocations = useMemo(() => {
    if (!projectSpace) return [];
    const byId = new Map(projectSpace.locations.map((l) => [l.id, l]));
    projectSpace.plots.forEach((p) => p.locations.forEach((l) => byId.set(l.id, l)));
    return [...byId.values()];
  }, [projectSpace]);

  const index = useMemo(() => (data ? buildMatrixIndex(data, allLocations) : null), [data, allLocations]);

  if ((loading && !data) || !projectSpace) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (error) return <div className="py-8 text-center text-sm text-red-700">{error}</div>;
  if (!data || !index || data.sections.length === 0 || allLocations.length === 0) {
    return <div className="py-8 text-center text-sm text-muted-foreground">Ei työvaiheita tai sijainteja</div>;
  }

  const plotLocationIds = new Set(projectSpace.plots.flatMap((p) => p.locations.map((l) => l.id)));
  const orphanLocations = projectSpace.locations.filter((l) => !plotLocationIds.has(l.id));

  return (
    <div className="space-y-8 py-5">
      {projectSpace.plots.map((plot) => (
        <ResponsibilityMatrixGrid
          key={plot.id}
          label={plot.name}
          locations={plot.locations}
          sections={data.sections.filter((s) => s.plotId === plot.id)}
          index={index}
          users={users}
          canEdit={canEdit}
          onAssign={assign}
        />
      ))}
      {orphanLocations.length > 0 && (
        <ResponsibilityMatrixGrid
          label="Hanketason tilat"
          locations={orphanLocations}
          sections={data.sections}
          index={index}
          users={users}
          canEdit={canEdit}
          onAssign={assign}
        />
      )}
    </div>
  );
}
